import { useState, useEffect, useRef } from "react";
import TherapistSideBar from "../../common/TherapistSidebar/TherapistSidebar";
import { useDispatch, useSelector } from "react-redux";
import {
  updateTherapist,
  getCurrentTherapist,
  changeTherapistPassword,
} from "../../redux/therapistSlice";
import { uploadImage } from "../../redux/uploadSlice"; // Upload slice'ı import et
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import getCroppedImg from "../../utils/cropImage";

const TherapistProfileSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const therapistState = useSelector((state) => state.therapist) || {};
  const { therapist, loading } = therapistState;

  // Upload state'ini de ekle
  const uploadState = useSelector((state) => state.upload) || {};
  const {
    image: uploadedImage,
    loading: uploadLoading,
    error: uploadError,
  } = uploadState;

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",

    phone: "",
    countryCode: "+90",
    email: "",
    profileImage: null,
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null); // Yüklenen resim URL'i
  const fileInputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Cropper state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  // Şifre görünürlük toggle fonksiyonları
  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    dispatch(getCurrentTherapist());
  }, [dispatch]);

  // Therapist verisi yüklendiğinde profileData state'ini güncelle
  useEffect(() => {
    if (therapist && !loading) {
      const effectiveContact = therapist.contact || {};
      const normalizedCountryCode = effectiveContact.countryCode
        ? String(effectiveContact.countryCode).startsWith("+")
          ? String(effectiveContact.countryCode)
          : `+${effectiveContact.countryCode}`
        : "+90";

      setProfileData({
        firstName: therapist.name || "",
        lastName: therapist.surname || "",
        email: therapist.email || "",
        phone: effectiveContact.phoneNumber || "",
        countryCode: normalizedCountryCode,

      });

      // Profil resmi kontrolü
      if (therapist.picture) {
        setPreviewImage(therapist.picture);
      }
    }
  }, [therapist, loading]);

  // Upload işlemi tamamlandığında
  useEffect(() => {
    if (uploadedImage && !uploadLoading && !uploadError) {
      // Hem location hem de imageUrl alanlarını kontrol et
      const imageUrl = uploadedImage.location || uploadedImage.imageUrl;
      if (imageUrl) {
        setUploadedImageUrl(imageUrl);
        toast.success(t("imageUploadSuccess"));
      }
    }

    if (uploadError) {
      toast.error(t("imageUploadError") + ": " + (uploadError.message || uploadError));
    }
  }, [uploadedImage, uploadLoading, uploadError, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Dosya tipini kontrol et (sadece resim dosyaları)
      if (!file.type.match("image.*")) {
        toast.error(t("invalidImageFile"));
        return;
      }

      // Dosya boyutunu kontrol et (örn. 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t("fileSizeError"));
        return;
      }

      // Preview için FileReader kullan
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImageUrl(null); // Reset when new file selected
        setImageToCrop(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(
        imageToCrop,
        croppedAreaPixels
      );
      
      const croppedFile = new File([croppedImageBlob], "profile_picture.jpg", {
        type: "image/jpeg",
      });

      // Preview için
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(croppedFile);

      // Dosyayı state'e kaydet
      setProfileData({
        ...profileData,
        profileImage: croppedFile,
      });

      // Eğer edit modundaysak resmi hemen yükle
      if (isEditing) {
        await uploadImageToServer(croppedFile);
      }

      setShowCropModal(false);
      setImageToCrop(null);
    } catch (e) {
      console.error(e);
      toast.error(t("imageCropError"));
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Resmi sunucuya yükleme fonksiyonu
  const uploadImageToServer = async (file) => {
    try {
      const formData = new FormData();
      // Backend route'da imageUpload.single("picture") olduğu için 'picture' kullanıyoruz
      formData.append("picture", file);

      // Upload slice'ındaki action'ı kullan
      const result = await dispatch(uploadImage(formData)).unwrap();

      return result;
    } catch (error) {
      console.error("Image upload error:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      let imageUrlToSend = null;

      // Eğer yeni bir resim seçildiyse ve henüz yüklenmediyse
      if (profileData.profileImage instanceof File && !uploadedImageUrl) {
        const uploadResult = await uploadImageToServer(
          profileData.profileImage
        );
        // Hem location hem de imageUrl'i kontrol et
        imageUrlToSend = uploadResult.location || uploadResult.imageUrl;
      } else if (uploadedImageUrl) {
        // Daha önce yüklenmiş resim varsa onu kullan
        imageUrlToSend = uploadedImageUrl;
      }

      // Profil güncelleme verisi
      const updateData = {
        name: profileData.firstName,
        surname: profileData.lastName,
        email: profileData.email,
      };

      // Sadece dolu ise contact gönder
      if (profileData.phone) {
        const rawCountryCode = String(profileData.countryCode || "+90").trim();
        const normalizedCountryCode = rawCountryCode.startsWith("+")
          ? rawCountryCode
          : `+${rawCountryCode}`;

        updateData.contact = {
          countryCode: normalizedCountryCode,
          phoneNumber: profileData.phone,
        };
      }

      // Resim URL'i varsa ekle
      if (imageUrlToSend) {
        updateData.picture = imageUrlToSend;
        updateData.profileImage = imageUrlToSend;
      }

      // Redux action ile güncellemeyi gönder
      await dispatch(updateTherapist(updateData)).unwrap();

      toast.success(t("profileUpdateSuccess"));
      setIsEditing(false);
      setUploadedImageUrl(null);
      await dispatch(getCurrentTherapist());
    } catch (error) {
      const message =
        error?.message ||
        error?.data?.message ||
        error?.error ||
        (typeof error === "string" ? error : null) ||
        "An error occurred while updating your profile.";
      toast.error(message);
      console.error("Update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);

    // Şifre alanlarının dolu olduğunu kontrol et
    if (
      !passwordData.oldPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      toast.error("Please fill in all fields.");
      setIsChangingPassword(false);
      return;
    }

    // Yeni şifre ve onay şifresinin eşleştiğini kontrol et
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New password and confirmation password do not match.");
      setIsChangingPassword(false);
      return;
    }

    // Eski şifre ile yeni şifre aynı olamaz
    if (passwordData.oldPassword === passwordData.newPassword) {
      toast.error("New password must be different from the old password.");
      setIsChangingPassword(false);
      return;
    }

    // Şifre güçlülük kontrolü
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      toast.error(
        "Password must be at least 8 characters, contain uppercase, lowercase and a number."
      );
      setIsChangingPassword(false);
      return;
    }

    try {
      // Redux action ile şifre değiştirme isteği gönder
      await dispatch(
        changeTherapistPassword({
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword,
        })
      ).unwrap();

      toast.success("Your password has been changed successfully!");

      // Formu temizle
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast.error(error || "An error occurred while changing the password.");
    } finally {
      setIsChangingPassword(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const toggleEdit = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      // Edit moduna geçerken uploaded image URL'i temizle
      setUploadedImageUrl(null);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="flex-shrink-0">
          <TherapistSideBar />
        </div>

        {/* Ana içerik */}
        <div className="flex-grow min-w-0 p-3 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {/* Profil Bilgileri Formu */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h1 className="text-2xl font-bold text-gray-800 mb-6">
                {t("informationProfile")}
              </h1>

              <form onSubmit={handleSubmit}>
                {/* Profil Fotoğrafı Bölümü */}
                <div className="mb-6 flex flex-col items-center md:flex-row md:items-start">
                  <div className="w-32 h-32 relative mb-4 md:mb-0 md:mr-6">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                      {previewImage ? (
                        <img
                          src={previewImage}
                          alt="Profil Img"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <svg
                          className="w-16 h-16 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>

                    {/* Upload loading indicator */}
                    {uploadLoading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                        <svg
                          className="animate-spin h-8 w-8 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </div>
                    )}

                    {isEditing && (
                      <div
                        className="absolute bottom-0 right-0 bg-background text-white rounded-full p-2 cursor-pointer shadow-md hover:bg-background"
                        onClick={triggerFileInput}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                  {isEditing && (
                    <div className="flex flex-col">
                      <h3 className="text-lg font-medium text-gray-700 mb-2">
                        {t("profilePhoto")}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">
                        {t("uploadImageInfo")}
                      </p>
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        disabled={uploadLoading}
                        className="px-4 py-2 text-sm font-medium text-white bg-button-bg rounded-md hover:bg-[#D0EFD0] focus:outline-none focus:ring-2 focus:ring-[#024202] mb-2 self-start disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {uploadLoading ? t("uploading") : t("selectPhoto")}
                      </button>
                      {uploadError && (
                        <p className="text-sm text-red-600">
                          {t("uploadError")}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-4">
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("firstName")}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border rounded-md ${
                        isEditing
                          ? "border-cards-border bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                          : "bg-gray-100 border-gray-300"
                      }`}
                      placeholder="Your first name"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {t("lastName")}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-full px-3 py-2 border rounded-md ${
                        isEditing
                          ? "border-cards-border bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                          : "bg-gray-100 border-gray-300"
                      }`}
                      placeholder="Your last name"
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleChange}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-md ${
                      isEditing
                        ? "border-cards-border bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                        : "bg-gray-100 border-gray-300"
                    }`}
                    placeholder="Your email address"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("phone")}
                  </label>
                  <div className="flex">
                    <select
                      name="countryCode"
                      value={profileData.countryCode}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`w-24 px-3 py-2 border rounded-l-md ${
                        isEditing
                          ? "border-cards-border bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                          : "bg-gray-100 border-gray-300"
                      }`}
                    >
                      <option value="+90">+90</option>
                      <option value="+1">+1</option>
                      <option value="+44">+44</option>
                      <option value="+49">+49</option>
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`flex-1 px-3 py-2 border-t border-b border-r rounded-r-md ${
                        isEditing
                          ? "border-cards-border bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                          : "bg-gray-100 border-gray-300"
                      }`}
                      placeholder="Your phone number"
                      required
                    />
                  </div>
                </div>



                <div className="flex justify-end space-x-3">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={toggleEdit}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        disabled={isSaving || uploadLoading}
                      >
                        {t("cancel")}
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-medium text-white bg-button-bg rounded-md hover:bg-cards-border focus:outline-none focus:ring-2 focus:ring-[#024202]"
                        disabled={isSaving || uploadLoading}
                      >
                        {isSaving ? (
                          <span className="flex items-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {t("saving")}
                          </span>
                        ) : uploadLoading ? (
                          <span className="flex items-center">
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            {t("uploading")}
                          </span>
                        ) : (
                          t("save")
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={toggleEdit}
                      className="px-4 py-2 text-sm font-medium text-white bg-button-bg rounded-md hover:bg-cards-border focus:outline-none focus:ring-2 focus:ring-[#024202]"
                    >
                      {t("edit")}
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Şifre Değiştirme Formu */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                {t("changePassword")}
              </h2>

              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-4">
                  <label
                    htmlFor="oldPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("oldPassword")}
                  </label>
                  <div className="relative">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      id="oldPassword"
                      name="oldPassword"
                      value={passwordData.oldPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-10 border border-[#024202] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                      placeholder={t("oldPasswordPlaceholder")}
                      required
                    />
                    <button
                      type="button"
                      onClick={toggleOldPasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showOldPassword ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M14.121 14.121L15.536 15.536M14.121 14.121L9.878 9.878m4.243 4.243L15.536 15.536M12 3c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21l-6-6m0 0l-3.879-3.879"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="newPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("newPassword")}
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-10 border border-[#024202] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                      placeholder={t("newPasswordPlaceholder")}
                      required
                    />
                    <button
                      type="button"
                      onClick={toggleNewPasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showNewPassword ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M14.121 14.121L15.536 15.536M14.121 14.121L9.878 9.878m4.243 4.243L15.536 15.536M12 3c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21l-6-6m0 0l-3.879-3.879"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t("passwordRequirements")}
                  </p>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t("confirmPassword")}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-10 border border-[#024202] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#024202]"
                      placeholder={t("confirmPasswordPlaceholder")}
                      required
                    />
                    <button
                      type="button"
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.464 8.464M14.121 14.121L15.536 15.536M14.121 14.121L9.878 9.878m4.243 4.243L15.536 15.536M12 3c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21l-6-6m0 0l-3.879-3.879"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-button-bg rounded-md hover:bg-cards-border focus:outline-none focus:ring-2 focus:ring-[#024202]"
                    disabled={isChangingPassword}
                  >
                    {isChangingPassword ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t("changingPassword")}
                      </span>
                    ) : (
                      t("changePassword")
                    )}
                  </button>
                </div>
              </form>
            </div>
        </div>
        </div>
      {/* Image Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-2 sm:p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl flex flex-col" style={{ maxHeight: "min(90vh, 600px)" }}>
            <div className="p-3 sm:p-4 border-b flex-shrink-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                {t("adjustPhoto")}
              </h2>
            </div>
            <div className="relative bg-gray-100 overflow-hidden" style={{ height: "min(55vh, 380px)" }}>
              <Cropper
                image={imageToCrop}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>
            <div className="p-3 sm:p-4 bg-white border-t flex-shrink-0">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("zoom")}
                </label>
                <input
                  type="range"
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  aria-labelledby="Zoom"
                  onChange={(e) => setZoom(e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#024202]"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCropCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  {t("cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleCropSave}
                  className="px-4 py-2 text-sm font-medium text-white bg-button-bg rounded-md hover:bg-opacity-90 transition-colors shadow-sm"
                >
                  {t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TherapistProfileSettings;
